import { usePaymentStatusDropdown } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/hooks/usePaymentStatusDropdown.ts';
import { PaymentStatusIndicator } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/PaymentStatusIndicator.tsx';
import {
	CaretContainer,
	StatusDropdownContainer,
	StatusDropdownHeader,
	StatusDropdownItem,
	StatusTriggerButton,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/Table.styles.ts';
import { PAYMENT_STATUSES } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/Table.utils.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import {
	DataMipPaymentRes,
	PermissionRole,
	ResourceType,
	TransactionState,
} from '@dealstack/shared/lib/generated';
import { Flex, Portal, Text } from '@mantine/core';
import { UpdatePaymentFn } from './Table.utils.types';

interface Props {
	payment: DataMipPaymentRes;
	updatePayment: UpdatePaymentFn;
	onOpenIncompleteDrawer: (paymentIds: string[], annotation?: string | null) => void;
}

export const PaymentStatusDropdown = ({
	payment,
	updatePayment,
	onOpenIncompleteDrawer,
}: Props) => {
	const { mipId } = useRequiredParams('mipId');
	const tMipManage = useTranslationMipManage();
	const nextStatuses = PAYMENT_STATUSES.filter((status) => status !== payment.paymentStatus);
	const showStatusDropdown = !payment.isInvestmentWorkflowCompleted;
	const { opened, targetRef, dropdownRef, dropdownPosition, toggleDropdown, closeDropdown } =
		usePaymentStatusDropdown();

	if (!showStatusDropdown) {
		return <PaymentStatusIndicator status={payment.paymentStatus} />;
	}

	return (
		<PermissionWrapper
			resourceType={ResourceType.PROGRAMME}
			resourceId={mipId}
			permissionRole={PermissionRole.EDITOR}
			loadingFallback={<PaymentStatusIndicator status={payment.paymentStatus} />}
		>
			{(hasPermission) =>
				hasPermission ? (
					<div ref={targetRef} style={{ position: 'relative', width: '100%' }}>
						<div
							style={{ width: '100%' }}
							onClick={(event) => {
								event.stopPropagation();
								toggleDropdown();
							}}
						>
							<StatusTriggerButton>
								<Flex align="center" justify="space-between" w="100%" gap={6}>
									<PaymentStatusIndicator status={payment.paymentStatus} />
									<CaretContainer $isActive={opened}>
										<Icon
											variant="caret-down"
											size="0.75rem"
											color={defaultTheme.v3.colors.black[0]}
										/>
									</CaretContainer>
								</Flex>
							</StatusTriggerButton>
						</div>

						{opened && (
							// it must be portal to correctly show it when open on last rows
							<Portal>
								<StatusDropdownContainer
									ref={dropdownRef}
									$openUp={dropdownPosition.openUp}
									style={{
										top: dropdownPosition.top,
										left: dropdownPosition.left,
										width: dropdownPosition.width,
									}}
									onClick={(event) => event.stopPropagation()}
								>
									<StatusDropdownHeader>
										<Text size="sm" c="gray.9">
											{tMipManage('payments.dropdown.change_status')}
										</Text>
									</StatusDropdownHeader>
									{nextStatuses.map((status) => (
										<StatusDropdownItem
											key={status}
											onClick={() => {
												closeDropdown();

												if (status === TransactionState.INCOMPLETE) {
													onOpenIncompleteDrawer(
														[payment.id],
														payment.paymentAnnotation
													);
													return;
												}

												updatePayment({
													id: payment.id,
													status,
												});
											}}
										>
											<PaymentStatusIndicator status={status} />
										</StatusDropdownItem>
									))}
								</StatusDropdownContainer>
							</Portal>
						)}
					</div>
				) : (
					<PaymentStatusIndicator status={payment.paymentStatus} />
				)
			}
		</PermissionWrapper>
	);
};
